"use client";

import { useEffect, useState } from "react";

const DISMISS_KEY = "bb_outdated_browser_dismissed";

/**
 * Feature-detects (rather than user-agent-sniffs) whether the current browser
 * is old enough that parts of the app may not render correctly — e.g. Safari
 * 13 on macOS 10.13 High Sierra, the last version Apple ever shipped there.
 * Checks a small set of APIs/CSS that only landed in engines from ~2022+
 * (Safari 15.4, Chrome 100-ish, Firefox 100-ish); missing any of them is a
 * reliable signal the browser predates that generation, independent of which
 * vendor or version string it reports.
 */
function isBrowserOutdated(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (typeof (globalThis as any).structuredClone !== "function") return true;
    if (!(window as any).ResizeObserver) return true;
    if (typeof CSS === "undefined" || typeof CSS.supports !== "function") return true;
    if (!CSS.supports("selector(:has(*))")) return true;
    if (!CSS.supports("aspect-ratio: 1 / 1")) return true;
    return false;
  } catch {
    return false;
  }
}

export function useOutdatedBrowser() {
  const [outdated, setOutdated] = useState(false);
  const [dismissed, setDismissed] = useState(true); // default true until we've checked, to avoid a flash

  useEffect(() => {
    setOutdated(isBrowserOutdated());
    setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore — worst case the banner reappears next visit
    }
  };

  return { show: outdated && !dismissed, dismiss };
}
